/**
 * HDB Portal — Sign in with mobile + 4-digit MPIN.
 * -------------------------------------------------
 *  • For customers who self-registered (no magic link).
 *  • Optional "Keep me signed in" checkbox flips a 1-day session → 30-day.
 *  • If they haven't set an MPIN yet, we send them via magic-link OR
 *    tell them to open the last WhatsApp link once to set it.
 *  • Mobile-first, single-column, big tap targets to match PortalRegister.
 */
import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import portalApi from "@/lib/portalApi";
import { safeNext } from "@/lib/plannerApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { toast } from "sonner";
import { Phone, KeyRound, Loader2, ArrowRight, MapPin } from "lucide-react";

const HDB_LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_pdf-guide-dev/artifacts/7s3f15d0_HDB%20LOGO.webp";

const cleanPhone = (v) => (v || "").replace(/\D/g, "").slice(-10);
const cleanMpin  = (v) => (v || "").replace(/\D/g, "").slice(0, 4);

export default function PortalSignIn() {
    const nav = useNavigate();
    const { refresh } = usePortalAuth();
    const [phone, setPhone] = useState("");
    const [mpin, setMpin] = useState("");
    const [remember, setRemember] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const mpinRef = useRef(null);

    const validPhone = phone.length === 10 && /^[6-9]/.test(phone);
    const validMpin  = mpin.length === 4;
    const canSubmit  = validPhone && validMpin;
    const hint = useMemo(() => {
        if (!validPhone) return "Enter your 10-digit mobile number";
        if (!validMpin)  return "Enter the 4-digit MPIN you set at signup";
        return "";
    }, [validPhone, validMpin]);

    // Auto-jump to MPIN once phone is complete
    useEffect(() => {
        if (validPhone && mpinRef.current) mpinRef.current.focus();
        // eslint-disable-next-line
    }, [validPhone]);

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) { toast.error(hint); return; }
        setSubmitting(true);
        try {
            await portalApi.post("/signin", {
                contact_phone: phone, mpin, remember_me: remember,
            });
            await refresh();
            toast.success("Welcome back!");
            nav(safeNext(new URLSearchParams(window.location.search).get("next")), { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail || "Sign-in failed";
            toast.error(detail);
            // If lockout, clear the MPIN to slow the user's next tap
            if (err.response?.status === 429) setMpin("");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50/40"
             data-testid="portal-signin-page">
            {/* Service-area strip — matches Register */}
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-center">
                <div className="max-w-md mx-auto text-[12px] flex items-center justify-center gap-2">
                    <MapPin size={13} className="text-amber-700"/>
                    <span className="font-black text-amber-900 uppercase tracking-wide">
                        Delivering in Dehradun &amp; Bangalore
                    </span>
                </div>
            </div>

            {/* Hero */}
            <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white
                            px-5 pt-8 pb-8 rounded-b-[36px] shadow-lg relative overflow-hidden">
                <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10 blur-2xl"/>
                <div className="relative">
                    <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-white p-2 shadow-lg grid place-items-center shrink-0">
                            <img src={HDB_LOGO} alt="Har Din Bachat"
                                 className="w-full h-full object-contain"/>
                        </div>
                        <div>
                            <div className="text-lg font-black tracking-tight leading-tight"
                                 style={{fontFamily:"'Chivo',sans-serif"}}>
                                Har Din Bachat
                            </div>
                            <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">
                                Welcome back
                            </div>
                        </div>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-black leading-tight mt-6"
                        style={{fontFamily:"'Chivo',sans-serif"}}>
                        Sign in with MPIN.
                    </h1>
                    <p className="text-emerald-50/95 mt-2 text-sm font-medium">
                        Enter your mobile + 4-digit MPIN. Fast and simple.
                    </p>
                </div>
            </div>

            <form onSubmit={submit}
                  className="max-w-md mx-auto px-5 -mt-4 pb-32 space-y-4">
                <section className="bg-white rounded-2xl shadow-md border border-slate-200 p-4 space-y-3.5">
                    <Label>Your registered mobile number</Label>
                    <BigInput
                        value={phone}
                        onChange={(e) => setPhone(cleanPhone(e.target.value))}
                        placeholder="10-digit number"
                        icon={<Phone size={16}/>}
                        prefix="+91"
                        inputMode="numeric"
                        maxLength={10}
                        autoFocus
                        data-testid="portal-signin-phone"
                    />
                    <Label>4-digit MPIN</Label>
                    <BigInput
                        ref={mpinRef}
                        value={mpin}
                        onChange={(e) => setMpin(cleanMpin(e.target.value))}
                        placeholder="• • • •"
                        icon={<KeyRound size={16}/>}
                        inputMode="numeric"
                        maxLength={4}
                        type="password"
                        data-testid="portal-signin-mpin"
                    />
                    <label className="flex items-center gap-2 pt-1 text-sm text-slate-700 cursor-pointer select-none">
                        <input type="checkbox" checked={remember}
                               onChange={(e) => setRemember(e.target.checked)}
                               className="w-4 h-4 accent-emerald-600"
                               data-testid="portal-signin-remember"/>
                        <span>Keep me signed in on this device (30 days)</span>
                    </label>
                </section>

                {/* Forgot MPIN + register — soft-nudge for people who mistyped the URL */}
                <div className="text-center text-xs text-slate-500 space-y-1">
                    <div>
                        Forgot your MPIN? Open your welcome link from HDB (WhatsApp / email) and reset it under Profile.
                    </div>
                    <div>
                        New here?{" "}
                        <Link to="/portal/register" className="font-bold text-emerald-700 underline"
                              data-testid="portal-signin-register-link">
                            Create an account
                        </Link>
                    </div>
                </div>

                {/* Sticky submit */}
                <div className="fixed inset-x-0 bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 shadow-2xl">
                    <div className="max-w-md mx-auto flex items-center gap-2">
                        <div className="flex-1 min-w-0 text-[11px] text-slate-500 truncate">
                            {canSubmit
                                ? <span className="text-emerald-700 font-bold">Ready. Tap to sign in.</span>
                                : hint}
                        </div>
                        <button type="submit" disabled={!canSubmit || submitting}
                                className={`px-6 py-3 rounded-xl font-black text-white text-sm shadow-lg transition-all
                                            ${canSubmit && !submitting
                                              ? "bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-500 active:scale-[0.98]"
                                              : "bg-slate-300 cursor-not-allowed"}`}
                                data-testid="portal-signin-submit">
                            {submitting
                                ? <><Loader2 size={14} className="inline animate-spin mr-1"/> Signing in…</>
                                : <>Sign in <ArrowRight size={14} className="inline ml-1"/></>}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

function Label({ children }) {
    return (
        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block">
            {children}
        </label>
    );
}

import { forwardRef } from "react";

const BigInput = forwardRef(function BigInput({ icon, prefix, ...rest }, ref) {
    return (
        <div className="flex items-center gap-2 px-3 rounded-xl border-2 border-slate-200 focus-within:border-emerald-500 transition-colors bg-white">
            {icon && <span className="text-slate-400 shrink-0">{icon}</span>}
            {prefix && <span className="text-slate-400 font-mono text-sm">{prefix}</span>}
            <input ref={ref}
                   className="flex-1 py-3 outline-none text-base bg-transparent min-w-0 tracking-widest"
                   {...rest}/>
        </div>
    );
});
